/**
 * Feedback detail drawer (ADR-0074 §3.4).
 *
 * Slides in from the right when a row is clicked. Renders message,
 * inline screenshot (when present), context as a labeled key/value
 * list (NEVER raw JSON — per `feedback_never_show_json_to_users.md`),
 * editable triage notes (autosave on blur — per
 * `feedback_dont_make_me_save.md`), and Close/Reopen action buttons.
 */

import { useEffect, useRef, useState } from "react";
import { apiFetch, ApiRequestError } from "../lib/api.js";
import { useToast } from "../components/Toast.js";
import type { FeedbackDetail, FeedbackStatus } from "../lib/feedback.js";

interface ContextBrowser {
  user_agent?: string;
  viewport_width?: number;
  viewport_height?: number;
  timezone?: string;
  locale?: string;
}

interface ContextActiveObject {
  kind?: string;
  display_name?: string | null;
  // intentionally NOT rendering id alone (per the no-UUID-in-UI rule)
}

interface ContextLastError {
  message?: string;
  at?: string;
  kind?: string;
}

interface FeedbackContextShape {
  route_path?: string;
  route_label?: string;
  active_object?: ContextActiveObject;
  last_error?: ContextLastError;
  browser?: ContextBrowser;
  build?: { hub_sha?: string | null; console_sha?: string | null };
  screenshot_capture_failed?: boolean;
}

function activeObjectLabel(obj: ContextActiveObject): string {
  const name = obj.display_name ?? "(unnamed)";
  switch (obj.kind) {
    case "pipeline": return `Pipeline · ${name}`;
    case "node": return `Node · ${name}`;
    case "run": return `Run · ${name}`;
    case "action_item": return `Action Item · ${name}`;
    case "query": return `Query · ${name}`;
    default: return name;
  }
}

function fmtBrowser(b: ContextBrowser): string {
  const parts: string[] = [];
  if (typeof b.user_agent === "string" && b.user_agent.trim().length > 0) parts.push(b.user_agent);
  if (typeof b.viewport_width === "number" && typeof b.viewport_height === "number") {
    parts.push(`${b.viewport_width}×${b.viewport_height}`);
  }
  if (typeof b.timezone === "string" && b.timezone.length > 0) parts.push(b.timezone);
  if (typeof b.locale === "string" && b.locale.length > 0) parts.push(b.locale);
  return parts.length > 0 ? parts.join(" · ") : "—";
}

function fmtBuild(b: FeedbackContextShape["build"]): string | null {
  if (!b) return null;
  const parts: string[] = [];
  if (b.hub_sha) parts.push(`hub ${b.hub_sha}`);
  if (b.console_sha) parts.push(`console ${b.console_sha}`);
  return parts.length > 0 ? parts.join(" · ") : null;
}

function fmtClosedAt(iso: string | null): string {
  if (!iso) return "";
  return new Date(iso).toLocaleString();
}

const TRIAGE_NOTES_DEBOUNCE_MS = 600;

export function AdminFeedbackDetail(props: {
  id: string;
  onClose: () => void;
  onChanged: () => void;
}) {
  const [detail, setDetail] = useState<FeedbackDetail | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [notesDraft, setNotesDraft] = useState("");
  const debounceRef = useRef<number | null>(null);
  const toast = useToast();

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await apiFetch<{ feedback: FeedbackDetail }>(`/api/admin/feedback/${props.id}`);
        if (cancelled) return;
        setDetail(res.feedback);
        setNotesDraft(res.feedback.triage_notes ?? "");
      } catch (e) {
        if (cancelled) return;
        setLoadError(e instanceof Error ? e.message : String(e));
      }
    })();
    return () => { cancelled = true; };
  }, [props.id]);

  // Close on Escape.
  useEffect(() => {
    function handler(e: KeyboardEvent) {
      if (e.key === "Escape") props.onClose();
    }
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, [props]);

  function scheduleNotesSave(value: string) {
    if (debounceRef.current !== null) window.clearTimeout(debounceRef.current);
    debounceRef.current = window.setTimeout(() => {
      void saveNotes(value);
    }, TRIAGE_NOTES_DEBOUNCE_MS);
  }

  async function saveNotes(value: string) {
    if (!detail) return;
    if ((detail.triage_notes ?? "") === value) return;
    try {
      const res = await apiFetch<{ feedback: FeedbackDetail }>(
        `/api/admin/feedback/${detail.id}`,
        { method: "PATCH", body: { triage_notes: value } }
      );
      setDetail(res.feedback);
    } catch (e) {
      toast.push({ kind: "error", message: e instanceof Error ? e.message : "Couldn't save triage notes." });
    }
  }

  async function transitionStatus(next: FeedbackStatus) {
    if (!detail || busy) return;
    setBusy(true);
    try {
      const res = await apiFetch<{ feedback: FeedbackDetail }>(
        `/api/admin/feedback/${detail.id}`,
        { method: "PATCH", body: { status: next } }
      );
      setDetail(res.feedback);
      props.onChanged();
      toast.push({ kind: "success", message: next === "closed" ? "Closed." : "Reopened." });
    } catch (e) {
      toast.push({ kind: "error", message: e instanceof Error ? e.message : "Couldn't update status." });
    } finally {
      setBusy(false);
    }
  }

  function copyUrl() {
    const ctx = (detail?.context ?? {}) as FeedbackContextShape;
    const path = ctx.route_path;
    if (typeof path !== "string" || path.length === 0) return;
    const fullUrl = location.origin + path;
    void navigator.clipboard.writeText(fullUrl).then(() => {
      toast.push({ kind: "success", message: "URL copied." });
    });
  }

  if (loadError) {
    return (
      <div style={drawerStyle}>
        <div className="card">
          <div className="actions"><button className="btn" onClick={props.onClose}>Close</button></div>
          <div className="help" style={{ color: "var(--danger-text)" }}>{loadError}</div>
        </div>
      </div>
    );
  }
  if (!detail) {
    return (
      <div style={drawerStyle}>
        <div className="muted" style={{ padding: 16 }}>Loading…</div>
      </div>
    );
  }

  const ctx = (detail.context ?? {}) as FeedbackContextShape;
  const buildLine = fmtBuild(ctx.build);

  return (
    <div style={drawerStyle} role="dialog" aria-label="Feedback detail">
      <div style={{ padding: 16, display: "grid", gap: 14 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
          <span className={severityPillClass(detail.severity)}>{detail.severity}</span>
          <span className="muted">{detail.tenant_display_name ?? "(no tenant)"}</span>
          <span className="muted">·</span>
          <span>{reporterLabel(detail)}</span>
          <span style={{ flex: 1 }} />
          <button
            type="button"
            className="btn btnSm"
            aria-label="Close inspector"
            title="Close inspector"
            onClick={props.onClose}
            style={{ minWidth: 24, paddingInline: 4, flexShrink: 0, fontSize: 12, lineHeight: 1 }}
          >
            ▶
          </button>
        </div>
        <div className="muted" style={{ fontSize: 12 }}>
          Submitted {new Date(detail.created_at).toLocaleString()} · Status: {detail.status}
        </div>

        <div className="card" style={{ display: "grid", gap: 8 }}>
          <div style={{ fontWeight: 600, fontSize: 13 }}>Message</div>
          <div style={{ whiteSpace: "pre-wrap", fontSize: 14 }}>{detail.message}</div>
        </div>

        {detail.has_screenshot ? (
          <div className="card">
            <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 8 }}>Screenshot</div>
            <a href={`/api/admin/feedback/${detail.id}/screenshot`} target="_blank" rel="noreferrer">
              <img
                src={`/api/admin/feedback/${detail.id}/screenshot`}
                alt="User-supplied screenshot"
                style={{ maxWidth: "100%", maxHeight: "60vh", border: "1px solid var(--border)" }}
              />
            </a>
          </div>
        ) : ctx.screenshot_capture_failed ? (
          <div className="card">
            <div className="muted">(screenshot capture failed on the client)</div>
          </div>
        ) : null}

        <div className="card" style={{ display: "grid", gap: 8 }}>
          <div style={{ fontWeight: 600, fontSize: 13 }}>Context</div>
          <div style={{ display: "grid", gridTemplateColumns: "120px 1fr", gap: 6, fontSize: 13 }}>
            <div className="muted">Route</div>
            <div>{ctx.route_label ?? ctx.route_path ?? "—"}</div>
            {typeof ctx.route_path === "string" && ctx.route_path.length > 0 ? (
              <>
                <div className="muted">URL</div>
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <span className="mono" style={{ fontSize: 12 }}>{ctx.route_path}</span>
                  <button type="button" className="btn" onClick={copyUrl}>Copy URL</button>
                </div>
              </>
            ) : null}
            {ctx.active_object ? (
              <>
                <div className="muted">Active</div>
                <div>{activeObjectLabel(ctx.active_object)}</div>
              </>
            ) : null}
            {ctx.last_error ? (
              <>
                <div className="muted">Last error</div>
                <div>{ctx.last_error.message ?? "—"}{ctx.last_error.at ? ` · ${new Date(ctx.last_error.at).toLocaleString()}` : ""}</div>
              </>
            ) : null}
            {ctx.browser ? (
              <>
                <div className="muted">Browser</div>
                <div>{fmtBrowser(ctx.browser)}</div>
              </>
            ) : null}
            {buildLine ? (
              <>
                <div className="muted">Build</div>
                <div>{buildLine}</div>
              </>
            ) : null}
            {detail.fingerprint ? (
              <>
                <div className="muted">Fingerprint</div>
                <div className="mono" style={{ fontSize: 11 }}>{detail.fingerprint.slice(0, 16)}…</div>
              </>
            ) : null}
          </div>
        </div>

        <div className="card" style={{ display: "grid", gap: 6 }}>
          <div style={{ fontWeight: 600, fontSize: 13 }}>Triage notes</div>
          <textarea
            className="input"
            rows={4}
            value={notesDraft}
            onChange={(e) => {
              setNotesDraft(e.target.value);
              scheduleNotesSave(e.target.value);
            }}
            onBlur={() => void saveNotes(notesDraft)}
            placeholder="Notes for other admins (autosaves)"
            style={{ width: "100%", resize: "vertical", minHeight: 80 }}
          />
        </div>

        <div className="actions" style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
          {detail.status === "open" ? (
            <button className="btn btnPrimary" disabled={busy} onClick={() => void transitionStatus("closed")}>Mark this case closed</button>
          ) : (
            <button className="btn" disabled={busy} onClick={() => void transitionStatus("open")}>Reopen this case</button>
          )}
        </div>

        {detail.status === "closed" && detail.closed_at ? (
          <div className="muted" style={{ fontSize: 12 }}>
            Closed on {fmtClosedAt(detail.closed_at)}
          </div>
        ) : null}
      </div>
    </div>
  );
}

function reporterLabel(detail: { account_display_name: string | null; account_email: string | null; account_id: string | null }): string {
  if (detail.account_display_name && detail.account_display_name.trim().length > 0) return detail.account_display_name;
  if (detail.account_email && detail.account_email.trim().length > 0) return detail.account_email;
  if (detail.account_id === null) return "(deleted user)";
  return "(unnamed user)";
}

function severityPillClass(s: string): string {
  if (s === "critical") return "pill pillBad";
  if (s === "major") return "pill pillWarn";
  return "pill";
}

const drawerStyle: React.CSSProperties = {
  position: "fixed",
  top: 0,
  right: 0,
  bottom: 0,
  width: "min(560px, 95vw)",
  background: "var(--surface, #fff)",
  borderLeft: "1px solid var(--border, #ddd)",
  boxShadow: "-8px 0 24px rgba(0,0,0,0.08)",
  overflowY: "auto",
  zIndex: 30,
};

// Re-export ApiRequestError-aware helper for the list page if needed.
export { ApiRequestError };
